import { useCallback, useEffect, useState } from 'react';

import type { OutlinerSettingsControlProps } from './components/OutlinerSettingsControl';
import type { OutlinerProps, OutlinerTreeAction } from './types';

export type OutlinerColumnLockMode = 'roles' | 'chains' | null;

interface UseOutlinerControlsOptions {
  columnLockMode: OutlinerColumnLockMode;
  hasRenderableSkeleton: boolean;
  isOutlinerPanelInteractable: boolean;
  isOutlinerSettingsAvailable: boolean;
  selectedBoneIndex: number | null;
}

type ManagedOutlinerProps = Pick<
  OutlinerProps,
  | 'autoRevealSelectedBone'
  | 'hideExcludedBones'
  | 'hideTransformNodes'
  | 'hideUnassignedBones'
  | 'onHoverChange'
  | 'showExcludedColumn'
  | 'showChainColumn'
  | 'showRolesColumn'
  | 'showTitleRow'
  | 'treeAction'
>;

interface UseOutlinerControlsResult {
  handleRevealSelectedBone: () => void;
  hotkeysEnabled: boolean;
  outlinerProps: ManagedOutlinerProps;
  settingsControlProps: OutlinerSettingsControlProps;
}

export default function useOutlinerControls({
  columnLockMode,
  hasRenderableSkeleton,
  isOutlinerPanelInteractable,
  isOutlinerSettingsAvailable,
  selectedBoneIndex,
}: UseOutlinerControlsOptions): UseOutlinerControlsResult {
  const [hideExcludedBones, setHideExcludedBones] = useState(false);
  const [hideUnassignedBones, setHideUnassignedBones] = useState(false);
  const [hideTransformNodes, setHideTransformNodes] = useState(true);
  const [autoRevealSelectedBone, setAutoRevealSelectedBone] = useState(true);
  const [showRolesColumn, setShowRolesColumn] = useState(true);
  const [showChainColumn, setShowChainColumn] = useState(true);
  const [showExcludedColumn, setShowExcludedColumn] = useState(false);
  const [showTitleRow, setShowTitleRow] = useState(true);
  const [outlinerTreeActionToken, setOutlinerTreeActionToken] = useState(0);
  const [outlinerTreeActionType, setOutlinerTreeActionType] = useState<
    OutlinerTreeAction['type'] | null
  >(null);
  const [isOutlinerHovered, setIsOutlinerHovered] = useState(false);
  const [isOutlinerSettingsOpen, setIsOutlinerSettingsOpen] = useState(false);

  useEffect(() => {
    if (isOutlinerSettingsAvailable) return;
    setIsOutlinerSettingsOpen(false);
  }, [isOutlinerSettingsAvailable]);

  useEffect(() => {
    if (isOutlinerPanelInteractable) return;
    setIsOutlinerHovered(false);
  }, [isOutlinerPanelInteractable]);

  const dispatchOutlinerTreeAction = useCallback(
    (type: OutlinerTreeAction['type']) => {
      setOutlinerTreeActionType(type);
      setOutlinerTreeActionToken((token) => token + 1);
    },
    [],
  );

  const handleRevealSelectedBone = useCallback(() => {
    if (selectedBoneIndex === null) return;
    dispatchOutlinerTreeAction('reveal-selected');
  }, [dispatchOutlinerTreeAction, selectedBoneIndex]);

  const outlinerTreeAction: OutlinerTreeAction | null = outlinerTreeActionType
    ? {
        type: outlinerTreeActionType,
        token: outlinerTreeActionToken,
      }
    : null;

  const outlinerColumnsLocked = columnLockMode !== null;
  const resolvedShowRolesColumn = outlinerColumnsLocked
    ? columnLockMode === 'roles'
    : showRolesColumn;
  const resolvedShowChainColumn = outlinerColumnsLocked
    ? columnLockMode === 'chains'
    : showChainColumn;

  const hotkeysEnabled =
    hasRenderableSkeleton && isOutlinerPanelInteractable && isOutlinerHovered;

  return {
    handleRevealSelectedBone,
    hotkeysEnabled,
    outlinerProps: {
      autoRevealSelectedBone,
      hideExcludedBones,
      hideTransformNodes,
      hideUnassignedBones,
      onHoverChange: (isHovered: boolean) => setIsOutlinerHovered(isHovered),
      showExcludedColumn,
      showChainColumn: resolvedShowChainColumn,
      showRolesColumn: resolvedShowRolesColumn,
      showTitleRow,
      treeAction: outlinerTreeAction,
    },
    settingsControlProps: {
      isOpen: isOutlinerSettingsOpen,
      autoRevealSelectedBone,
      hideExcludedBones,
      hideUnassignedBones,
      hideTransformNodes,
      showExcludedColumn,
      showRolesColumn: resolvedShowRolesColumn,
      showChainColumn: resolvedShowChainColumn,
      showTitleRow,
      columnVisibilityLocked: outlinerColumnsLocked,
      onToggleOpen: () => setIsOutlinerSettingsOpen((open) => !open),
      onClose: () => setIsOutlinerSettingsOpen(false),
      onExpandAll: () => dispatchOutlinerTreeAction('expand-all'),
      onCollapseAll: () => dispatchOutlinerTreeAction('collapse-all'),
      onToggleHideExcludedBones: () =>
        setHideExcludedBones((enabled) => !enabled),
      onToggleHideUnassignedBones: () =>
        setHideUnassignedBones((enabled) => !enabled),
      onToggleHideTransformNodes: () =>
        setHideTransformNodes((enabled) => !enabled),
      onToggleShowExcludedColumn: () =>
        setShowExcludedColumn((visible) => !visible),
      onToggleShowRolesColumn: () => {
        if (outlinerColumnsLocked) return;
        setShowRolesColumn((visible) => !visible);
      },
      onToggleShowChainColumn: () => {
        if (outlinerColumnsLocked) return;
        setShowChainColumn((visible) => !visible);
      },
      onToggleShowTitleRow: () => setShowTitleRow((visible) => !visible),
      onToggleAutoRevealSelectedBone: () =>
        setAutoRevealSelectedBone((enabled) => !enabled),
    },
  };
}
